import React, { useState, useContext, useEffect } from "react";

import { DataGrid, GridColDef, jaJP } from "@mui/x-data-grid";

import moment from "moment";

import { ReloadContext } from "../../context/ReloadContext";
import { renderCellExpand } from "../Common/GridCellExpand";
import {
  KaishaBaitaiShosaiDataSet,
  KaishaBaitaiShosaiData,
  KaishaBaitaiShosaiSearchParam,
} from "../../types/WebData";
import { postSearchKaishaBaitaiShosai } from "../../api/kaisha";

/**
 * DataGrid用にデータを編集
 *
 * @param {KaishaBaitaiShosaiDatas[]} kaishaBaitaiShosaiDatas
 * @return {*}
 */
const makeGridData = (
  kaishaBaitaiShosaiDatas: KaishaBaitaiShosaiDataSet[],
  setSelectionModel: React.Dispatch<React.SetStateAction<number[]>>
) => {
  const selectedList = [] as number[];
  const kaishaBaitaiShosaiList = kaishaBaitaiShosaiDatas.map((d) => {
    // 日付フォーマット
    const updateDate = moment(d.update_dt);

    if (d.is_selected) {
      selectedList.push(d.id);
    }
    return {
      ...d,
      update_dt: updateDate.format("YYYY/MM/DD"),
    };
  });

  setSelectionModel(selectedList);
  return kaishaBaitaiShosaiList;
};

/**
 * 全件データを１ページ分の行にsliceしている
 * 値に単位をつけるなどのデータの編集も、ここで１ページ分のみ行う
 */
const loadServerRows = (
  page: number,
  pageSize: number,
  allRows: KaishaBaitaiShosaiDataSet[],
  setSelectionModel: React.Dispatch<React.SetStateAction<number[]>>
): Promise<KaishaBaitaiShosaiDataSet[]> =>
  new Promise<KaishaBaitaiShosaiDataSet[]>((resolve) => {
    if (!allRows) return;
    resolve(
      // １ページ分にsliceした後に、データを編集するfunctionに渡している
      makeGridData(
        allRows.slice(page * pageSize, (page + 1) * pageSize),
        setSelectionModel
      )
    );
  });

const useQuery = (
  page: number,
  pageSize: number,
  allRows: any[],
  setSelectionModel: React.Dispatch<React.SetStateAction<number[]>>
) => {
  const [rowCount, setRowCount] = React.useState<number | undefined>(undefined);
  const [data, setData] = React.useState<KaishaBaitaiShosaiDataSet[]>([]);

  useEffect(() => {
    let active = true;

    setRowCount(undefined);
    loadServerRows(page, pageSize, allRows, setSelectionModel).then(
      (newRows) => {
        if (!active) {
          return;
        }
        setData(newRows);
        setRowCount(allRows.length);
      }
    );

    return () => {
      active = false;
    };
  }, [page, pageSize, allRows]);

  return { data, rowCount };
};

interface RowsState {
  page: number;
  pageSize: number;
}

type Props = {
  searchParam: KaishaBaitaiShosaiSearchParam | null;
  setload: React.Dispatch<React.SetStateAction<boolean>>;
  setRows: React.Dispatch<
    React.SetStateAction<KaishaBaitaiShosaiDataSet[] | null>
  >;
};

/**
 * 会社媒体詳細紐付け設定一覧コンポーネント
 *
 * @return {searchParam}
 */
export const KaishaBaitaiShosaiGrid = ({
  searchParam,
  setload,
  setRows,
}: Props) => {
  const reloadContext = useContext(ReloadContext);
  const [selectionModel, setSelectionModel] = useState<number[]>([]);
  const [mansionStaffData, setKaishaBaitaiShosaiData] =
    useState<KaishaBaitaiShosaiData>({ data: [] });

  useEffect(() => {
    const getSearchData = async (param: KaishaBaitaiShosaiSearchParam) => {
      const result = await postSearchKaishaBaitaiShosai(param);
      setKaishaBaitaiShosaiData(result);
      if (result?.isError) {
        await reloadContext?.setSnackbarInfo({
          isOpen: true,
          type: "error",
          message: result.message,
        });
      }
      setload(false);
    };

    if (!searchParam) return setload(false);
    getSearchData(searchParam);
  }, [searchParam, reloadContext?.reload]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      hide: true,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "gamen_mei",
      headerName: "画面名",
      width: 200,
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderCellExpand,
    },
  ];

  // ページのstate　１ページに１０行
  const [rowsState, setRowsState] = React.useState<RowsState>({
    page: 0,
    pageSize: 10,
  });

  const { data, rowCount } = useQuery(
    rowsState.page,
    rowsState.pageSize,
    mansionStaffData.data,
    setSelectionModel
  );

  // データの読み込みが間に合ってなくてrowCountがundefinedのままの場合の対策
  const [rowCountState, setRowCountState] = useState(rowCount || 0);
  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      rowCount !== undefined ? rowCount : prevRowCountState
    );
  }, [rowCount, setRowCountState]);

  useEffect(() => {
    setRows(mansionStaffData.data);
  }, [mansionStaffData]);

  return (
    <div style={{ height: 650, width: "100%" }}>
      <DataGrid
        rows={data}
        columns={columns}
        rowCount={rowCountState}
        {...rowsState}
        rowsPerPageOptions={[10]}
        pagination
        paginationMode="server"
        checkboxSelection
        disableSelectionOnClick
        selectionModel={selectionModel}
        keepNonExistentRowsSelected
        onSelectionModelChange={(newSelectionModel: any[]) => {
          setSelectionModel(newSelectionModel);

          // 全件データに対してページ内データだけ選択状態を更新する
          const newData = mansionStaffData.data.map((d) => {
            // 現在のページ内のデータに対してのみ選択状態を更新
            const isOnCurrentPage = data.some(
              (pageItem) => pageItem.id === d.id
            );
            if (isOnCurrentPage) {
              return {
                ...d,
                is_selected: newSelectionModel.includes(d.id),
              };
            } else {
              return d;
            }
          });
          setKaishaBaitaiShosaiData({ data: newData });
        }}
        sortModel={[{ field: "id", sort: "asc" }]}
        onPageChange={(page) => setRowsState((prev) => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) =>
          setRowsState((prev) => ({ ...prev, pageSize }))
        }
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
      />
    </div>
  );
};
